import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
  CalendarDays,
  ChevronDown,
  Download,
  Eye,
  Trash2,
  Clock,
  ArrowUpDown,
  AlertTriangle,
  RotateCcw,
  Undo2,
  X,
  FileSpreadsheet,
  FileCode,
} from 'lucide-react'
import { cn, formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FilterableSelect, type FilterableSelectOption } from '@/components/ui/filterable-select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { InvoiceUploadSection } from './invoice-sections/InvoiceUploadSection'
import { apiClient } from '@/lib/api'
import { exportInvoice, fetchClients, reprocessInvoice, reprocessInvoicesBulk, type ClientSummary } from '@/services/invoiceService'
import { ReprocessConfirmDialog } from './ReprocessConfirmDialog'
import { DocumentViewer } from './DocumentViewer'
import { useToast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/use-debounce'
import { format, parseISO } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { getInvoiceStatusBadge, statusBadgesList } from '@/lib/invoice-status'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { NotesCell } from './InvoiceNotes/NotesCell'

interface Client {
  id: string
  name: string
  status: string
  mssql_client_id?: string
}



type KontirovkaStatus = 'complete' | 'dma' | 'manual' | 'mixed'

interface Invoice {
  id: string
  original_filename: string
  file_path: string
  file_size: number
  mime_type: string
  status: 'uploaded' | 'processing' | 'completed' | 'failed' | 'review_required' | 'reviewed' | 'exported'
  overall_confidence?: number
  invoice_type?: string
  invoice_date?: string
  uploaded_at: string
  processed_at?: string
  error_message?: string
  client?: Client
  kontirovka_status?: KontirovkaStatus
  mirage_followup_done?: boolean
  latest_note?: string | null
  latest_note_at?: string | null
  note_count?: number
}

interface InvoiceFilters {
  status: string
  client_id: string
  invoice_type: string
  search: string
  uploaded_from: string
  uploaded_to: string
  invoice_date_from: string
  invoice_date_to: string
  kontirovka_status: 'all' | 'dma' | 'manual' | 'mixed' | 'any_missing' | 'none'
  mirage_followup: 'all' | 'pending' | 'done'
}

const DEFAULT_FILTERS: InvoiceFilters = {
  status: 'all',
  client_id: '',
  invoice_type: '',
  search: '',
  uploaded_from: '',
  uploaded_to: '',
  invoice_date_from: '',
  invoice_date_to: '',
  kontirovka_status: 'all',
  mirage_followup: 'all',
}

interface ReviewContext {
  type: 'selected' | 'all_pending'
  invoiceIds: string[]
}

interface InvoiceProcessingProps {
  onInvoiceSelect?: (invoiceId: string) => void
  onInvoiceReview?: (invoiceId: string, context?: ReviewContext) => void
  onFilesUploaded?: (files: { invoiceId: string; filename: string }[]) => void
  maxFiles?: number
  className?: string
}

interface PaginationInfo {
  total: number
  page: number
  limit: number
  total_pages: number
}



const setsAreEqual = (setA: Set<string>, setB: Set<string>) => {
  if (setA.size !== setB.size) {
    return false
  }

  for (const value of setA) {
    if (!setB.has(value)) {
      return false
    }
  }

  return true
}

export function InvoiceProcessing({
  onInvoiceReview,
  onFilesUploaded,
  maxFiles = 50,
  className
}: InvoiceProcessingProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<InvoiceFilters>(() => ({
    status: searchParams.get('status') || DEFAULT_FILTERS.status,
    client_id: searchParams.get('client_id') || DEFAULT_FILTERS.client_id,
    invoice_type: searchParams.get('invoice_type') || DEFAULT_FILTERS.invoice_type,
    search: searchParams.get('search') || DEFAULT_FILTERS.search,
    uploaded_from: searchParams.get('uploaded_from') || DEFAULT_FILTERS.uploaded_from,
    uploaded_to: searchParams.get('uploaded_to') || DEFAULT_FILTERS.uploaded_to,
    invoice_date_from: searchParams.get('invoice_date_from') || DEFAULT_FILTERS.invoice_date_from,
    invoice_date_to: searchParams.get('invoice_date_to') || DEFAULT_FILTERS.invoice_date_to,
    kontirovka_status: (searchParams.get('kontirovka_status') as InvoiceFilters['kontirovka_status']) || DEFAULT_FILTERS.kontirovka_status,
    mirage_followup: (searchParams.get('mirage_followup') as InvoiceFilters['mirage_followup']) || DEFAULT_FILTERS.mirage_followup,
  }))
  const [mirageFollowupPending, setMirageFollowupPending] = useState<number>(0)
  const [followupBusy, setFollowupBusy] = useState<Set<string>>(new Set())
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '')
  const debouncedSearch = useDebounce(searchInput, 400)
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'confidence'>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set())
  const lastSelectedIndex = useRef<number | null>(null)
  const [clients, setClients] = useState<ClientSummary[]>([])

  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    page: parseInt(searchParams.get('page') || '1', 10),
    limit: parseInt(searchParams.get('limit') || '50', 10),
    total_pages: 0
  })
  const [rowsPerPageInput, setRowsPerPageInput] = useState(String(parseInt(searchParams.get('limit') || '50', 10)))
  const [rowsPerPageOpen, setRowsPerPageOpen] = useState(false)

  // Polling state
  const [isPolling, setIsPolling] = useState(false)
  const [processingInvoices, setProcessingInvoices] = useState<Set<string>>(new Set())
  const pollRef = useRef<() => void>(() => { })
  const [pendingReviewTotal, setPendingReviewTotal] = useState<number>(0)
  const [pendingReviewInvoices, setPendingReviewInvoices] = useState<Invoice[]>([])

  // Upload functionality state for reusable components
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])
  const [isUploading, setIsUploading] = useState(false)

  // Bulk operations state
  const [isDeleting, setIsDeleting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)
  const [exportingInvoices, setExportingInvoices] = useState<Set<string>>(new Set())
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<'bulk' | string | null>(null)
  const [showBulkReprocessConfirm, setShowBulkReprocessConfirm] = useState(false)
  const [showSingleReprocessConfirm, setShowSingleReprocessConfirm] = useState(false)
  const [singleReprocessInvoiceId, setSingleReprocessInvoiceId] = useState<string | null>(null)
  const [isReprocessing, setIsReprocessing] = useState(false)
  const [showReturnToReviewConfirm, setShowReturnToReviewConfirm] = useState(false)
  const [returnToReviewInvoiceId, setReturnToReviewInvoiceId] = useState<string | null>(null)
  const [isReturningToReview, setIsReturningToReview] = useState(false)

  const location = useLocation()
  const { toast } = useToast()

  // Document viewer modal state
  const [viewerModalOpen, setViewerModalOpen] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [viewerFileViewer, setViewerFileViewer] = useState<{
    isLoadingFile: boolean
    fileError: string | null
    fileUrl: string | null
  }>({
    isLoadingFile: false,
    fileError: null,
    fileUrl: null
  })
  const [viewerZoom, setViewerZoom] = useState({
    zoomLevel: 100,
    zoomIn: () => setViewerZoom(prev => ({ ...prev, zoomLevel: Math.min(prev.zoomLevel + 25, 400) })),
    zoomOut: () => setViewerZoom(prev => ({ ...prev, zoomLevel: Math.max(prev.zoomLevel - 25, 25) }))
  })
  const filterKey = JSON.stringify(filters)

  // Sync filters and pagination to URL query params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(filters)) {
        if (value && value !== DEFAULT_FILTERS[key as keyof InvoiceFilters]) {
          params.set(key, value)
        } else {
          params.delete(key)
        }
      }
      if (pagination.page > 1) params.set('page', String(pagination.page))
      else params.delete('page')
      if (pagination.limit !== 50) params.set('limit', String(pagination.limit))
      else params.delete('limit')
      return params
    }, { replace: true })
  }, [filterKey, pagination.page, pagination.limit, setSearchParams])

  useEffect(() => {
    loadInvoices()
  }, [pagination.page, pagination.limit, filterKey])

  // Sync rowsPerPageInput when pagination.limit changes externally (e.g. URL)
  useEffect(() => {
    setRowsPerPageInput(String(pagination.limit))
  }, [pagination.limit])

  const applyRowsPerPage = () => {
    const num = parseInt(rowsPerPageInput, 10)
    if (isNaN(num) || num < 1) {
      setRowsPerPageInput(String(pagination.limit))
      return
    }
    const clamped = Math.min(Math.max(num, 1), 500)
    setRowsPerPageInput(String(clamped))
    setPagination(prev => ({ ...prev, limit: clamped, page: 1 }))
  }

  // Sync debounced search into filters
  useEffect(() => {
    setFilters(prev => {
      if (prev.search === debouncedSearch) return prev
      setPagination(p => ({ ...p, page: 1 }))
      return { ...prev, search: debouncedSearch }
    })
  }, [debouncedSearch])

  // Load clients for filter dropdown
  useEffect(() => {
    fetchClients({ hasInvoices: true }).then(setClients).catch(console.error)
  }, [])

  const updateFilter = useCallback((key: keyof InvoiceFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [])

  const clientOptions: FilterableSelectOption[] = useMemo(() =>
    clients.map(c => ({
      value: c.id,
      label: c.name,
      details: c.vat_code,
    })),
    [clients]
  )

  const hasActiveFilters = useMemo(() => {
    return filters.status !== 'all' ||
      filters.client_id !== '' ||
      filters.invoice_type !== '' ||
      filters.uploaded_from !== '' ||
      filters.uploaded_to !== '' ||
      filters.invoice_date_from !== '' ||
      filters.invoice_date_to !== '' ||
      searchInput !== ''
  }, [filters, searchInput])

  // Restore selected invoices from localStorage on component mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('selectedInvoices')
      if (stored) {
        const selectedIds = JSON.parse(stored)
        setSelectedInvoices(new Set(selectedIds))
      }
    } catch (error) {
      console.error('Error restoring selected invoices:', error)
      localStorage.removeItem('selectedInvoices')
    }
  }, [])

  useEffect(() => {
    try {
      const storedCount = localStorage.getItem('pendingReviewCount')
      if (storedCount) {
        const parsed = Number(storedCount)
        if (!Number.isNaN(parsed)) {
          setPendingReviewTotal(parsed)
        }
      }
    } catch (error) {
      console.error('Error restoring pending review count:', error)
      localStorage.removeItem('pendingReviewCount')
    }
  }, [])

  // Clear selected invoices when navigating away from this page
  useEffect(() => {
    return () => {
      localStorage.removeItem('selectedInvoices')
    }
  }, [])

  // Polling effect for processing invoices
  useEffect(() => {
    let intervalId: number | null = null

    if (isPolling && processingInvoices.size > 0) {
      intervalId = window.setInterval(() => {
        pollRef.current()
      }, 5000) // Poll every 5 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isPolling, processingInvoices.size])


  // Check for navigation state to trigger review all pending
  const fetchPendingReviewData = useCallback(async (): Promise<Invoice[]> => {
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: pagination.limit.toString(),
        status: 'review_required'
      })

      // Apply active filters so the count respects them
      if (filters.client_id) params.append('client_id', filters.client_id)
      if (filters.invoice_type) params.append('invoice_type', filters.invoice_type)
      if (filters.search) params.append('search', filters.search)
      if (filters.uploaded_from) params.append('uploaded_from', filters.uploaded_from)
      if (filters.uploaded_to) params.append('uploaded_to', filters.uploaded_to)
      if (filters.invoice_date_from) params.append('invoice_date_from', filters.invoice_date_from)
      if (filters.invoice_date_to) params.append('invoice_date_to', filters.invoice_date_to)

      const response = await apiClient.fetch(`/invoices/?${params}`)

      if (response.ok) {
        const data = await response.json()
        const invoicesForReview: Invoice[] = data.invoices || []
        const total = typeof data.total === 'number' ? data.total : invoicesForReview.length

        setPendingReviewInvoices(invoicesForReview)
        setPendingReviewTotal(total)
        localStorage.setItem('pendingReviewCount', total.toString())

        return invoicesForReview
      }
    } catch (error) {
      console.error('Error fetching pending review invoices:', error)
    }

    return pendingReviewInvoices
  }, [pagination.limit, pendingReviewInvoices, filters])

  useEffect(() => {
    const state = location.state as { triggerReviewAll?: boolean } | null
    if (!state?.triggerReviewAll) {
      return
    }

    const executeReviewAll = async () => {
      let pendingInvoices = invoices.filter(inv => inv.status === 'review_required')

      if (pendingInvoices.length === 0) {
        pendingInvoices = pendingReviewInvoices
      }

      if (pendingInvoices.length === 0) {
        pendingInvoices = await fetchPendingReviewData()
      }

      if (pendingInvoices.length > 0 && onInvoiceReview) {
        const pendingIds = pendingInvoices.map(inv => inv.id)
        onInvoiceReview(pendingInvoices[0].id, { type: 'all_pending', invoiceIds: pendingIds })
      }
    }

    void executeReviewAll()
  }, [location.state, invoices, onInvoiceReview, pendingReviewInvoices, fetchPendingReviewData])



  const setMirageFollowup = async (invoiceId: string, done: boolean) => {
    setFollowupBusy(prev => {
      const next = new Set(prev)
      next.add(invoiceId)
      return next
    })
    try {
      const response = await apiClient.fetch(`/invoices/${invoiceId}/mirage-followup`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ done }),
      })
      if (response.ok) {
        await loadInvoices()
      }
    } catch (err) {
      console.error('Failed to update mirage follow-up flag', err)
    } finally {
      setFollowupBusy(prev => {
        const next = new Set(prev)
        next.delete(invoiceId)
        return next
      })
    }
  }

  const loadInvoices = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      })

      if (filters.status !== 'all') params.append('status', filters.status)
      if (filters.client_id) params.append('client_id', filters.client_id)
      if (filters.invoice_type) params.append('invoice_type', filters.invoice_type)
      if (filters.search) params.append('search', filters.search)
      if (filters.uploaded_from) params.append('uploaded_from', filters.uploaded_from)
      if (filters.uploaded_to) params.append('uploaded_to', filters.uploaded_to)
      if (filters.invoice_date_from) params.append('invoice_date_from', filters.invoice_date_from)
      if (filters.invoice_date_to) params.append('invoice_date_to', filters.invoice_date_to)
      if (filters.kontirovka_status && filters.kontirovka_status !== 'all') {
        params.append('kontirovka_status', filters.kontirovka_status)
      }
      if (filters.mirage_followup && filters.mirage_followup !== 'all') {
        params.append('mirage_followup', filters.mirage_followup)
      }

      const response = await apiClient.fetch(`/invoices/?${params}`)

      if (response.ok) {
        const data = await response.json()
        const fetchedInvoices: Invoice[] = data.invoices || []
        setInvoices(fetchedInvoices)
        setPagination(prev => ({
          ...prev,
          total: data.total || 0,
          total_pages: data.total_pages || 0
        }))
        setMirageFollowupPending(data.counts?.mirage_followup_pending ?? 0)
        // Detect processing/uploaded invoices from fetched data and auto-resume polling
        const activelyProcessing = new Set(
          fetchedInvoices
            .filter((inv: Invoice) => inv.status === 'processing' || inv.status === 'uploaded')
            .map((inv: Invoice) => inv.id)
        )

        setProcessingInvoices(prev => {
          // Merge: keep tracked IDs that are still processing + add newly discovered ones
          const merged = new Set<string>()

          prev.forEach(id => {
            const invoice = fetchedInvoices.find((inv: Invoice) => inv.id === id)
            if (!invoice || invoice.status === 'processing' || invoice.status === 'uploaded') {
              merged.add(id)
            }
          })

          activelyProcessing.forEach(id => merged.add(id))

          if (merged.size === 0 && prev.size > 0) {
            setIsPolling(false)
          }

          return setsAreEqual(prev, merged) ? prev : merged
        })

        if (activelyProcessing.size > 0) {
          setIsPolling(true)
        }

        // Use the pending review count from the backend response
        setPendingReviewTotal(data.pending_review_count ?? 0)
      }
    } catch (error) {
      console.error('Error loading invoices:', error)
    } finally {
      setLoading(false)
    }
  }

  const pollProcessingInvoices = async () => {
    try {
      // Fetch current page with active filters to update the visible table
      const params = new URLSearchParams({
        page: String(pagination.page),
        limit: String(pagination.limit)
      })
      if (filters.status !== 'all') params.append('status', filters.status)
      if (filters.client_id) params.append('client_id', filters.client_id)
      if (filters.invoice_type) params.append('invoice_type', filters.invoice_type)
      if (filters.search) params.append('search', filters.search)
      if (filters.uploaded_from) params.append('uploaded_from', filters.uploaded_from)
      if (filters.uploaded_to) params.append('uploaded_to', filters.uploaded_to)
      if (filters.invoice_date_from) params.append('invoice_date_from', filters.invoice_date_from)
      if (filters.invoice_date_to) params.append('invoice_date_to', filters.invoice_date_to)

      const response = await apiClient.fetch(`/invoices/?${params}`)

      if (response.ok) {
        const data = await response.json()
        const updatedInvoices: Invoice[] = data.invoices || []

        setInvoices(updatedInvoices)
        setPagination(prev => ({
          ...prev,
          total: data.total || 0,
          total_pages: data.total_pages || 0
        }))

        // Check if any tracked processing invoices have finished
        const stillProcessing = new Set(
          updatedInvoices
            .filter((inv: Invoice) => inv.status === 'processing' || inv.status === 'uploaded')
            .map((inv: Invoice) => inv.id)
        )

        setProcessingInvoices(prev => {
          if (prev.size === 0 && stillProcessing.size === 0) return prev

          // Keep tracked IDs that aren't in the current page (might still be processing)
          // plus any that are confirmed still processing on this page
          const updatedSet = new Set<string>()
          const pageIds = new Set(updatedInvoices.map((inv: Invoice) => inv.id))

          prev.forEach(id => {
            if (!pageIds.has(id)) {
              // Not on current page — keep tracking (can't confirm status)
              updatedSet.add(id)
            } else if (stillProcessing.has(id)) {
              updatedSet.add(id)
            }
          })

          // Also add any newly discovered processing invoices
          stillProcessing.forEach(id => updatedSet.add(id))

          if (updatedSet.size === 0 && prev.size > 0) {
            setIsPolling(false)
          }

          return setsAreEqual(prev, updatedSet) ? prev : updatedSet
        })

        setPendingReviewTotal(data.pending_review_count ?? 0)
      }
    } catch (error) {
      console.error('Error polling invoices:', error)
    }
  }

  // Keep pollRef up-to-date so the interval always calls the latest closure
  pollRef.current = pollProcessingInvoices

  const confirmDeleteInvoice = (invoiceId: string) => {
    setDeleteConfirmTarget(invoiceId)
  }

  const deleteInvoice = async (invoiceId: string) => {
    try {
      const response = await apiClient.fetch(`/invoices/${invoiceId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setInvoices(prev => prev.filter(inv => inv.id !== invoiceId))
        // Remove from selection if it was selected
        setSelectedInvoices(prev => {
          const newSelection = new Set(prev)
          newSelection.delete(invoiceId)
          return newSelection
        })
        setProcessingInvoices(prev => {
          if (!prev.has(invoiceId)) {
            return prev
          }
          const updated = new Set(prev)
          updated.delete(invoiceId)
          if (updated.size === 0) {
            setIsPolling(false)
          }
          return updated
        })
        await fetchPendingReviewData()
      }
    } catch (error) {
      console.error('Error deleting invoice:', error)
    }
  }

  const handleBulkAction = (action: 'delete' | 'export') => {
    if (selectedInvoices.size === 0) return

    if (action === 'delete') {
      setDeleteConfirmTarget('bulk')
    } else if (action === 'export') {
      handleBatchExport()
    }
  }

  const handleBulkDelete = async () => {
    if (selectedInvoices.size === 0) return

    setDeleteConfirmTarget(null)
    setIsDeleting(true)

    try {
      const selectedIds = Array.from(selectedInvoices)
      const deletePromises = selectedIds.map(invoiceId =>
        apiClient.fetch(`/invoices/${invoiceId}`, {
          method: 'DELETE'
        })
      )

      const results = await Promise.allSettled(deletePromises)

      // Filter out successfully deleted invoices
      const failedDeletions: string[] = []
      results.forEach((result, index) => {
        if (result.status === 'rejected' || !result.value.ok) {
          failedDeletions.push(selectedIds[index])
        }
      })

      // Update state after deletions
      const successfullyDeleted = selectedIds.filter(id => !failedDeletions.includes(id))
      if (successfullyDeleted.length > 0) {
        // Clear processing state for deleted invoices
        setProcessingInvoices(prev => {
          if (prev.size === 0) return prev
          const updated = new Set(prev)
          let changed = false
          successfullyDeleted.forEach(id => { if (updated.delete(id)) changed = true })
          if (!changed) return prev
          if (updated.size === 0) setIsPolling(false)
          return updated
        })
        // Refetch current page — cross-page deletions won't be in local invoices array
        await loadInvoices()
        await fetchPendingReviewData()
      }

      // Show results and update selection
      if (failedDeletions.length > 0) {
        setSelectedInvoices(new Set(failedDeletions))
        alert(`Successfully deleted ${successfullyDeleted.length} invoices. Failed to delete ${failedDeletions.length} invoices.`)
      } else {
        setSelectedInvoices(new Set())
      }
    } catch (error) {
      console.error('Error in bulk delete:', error)
      alert(`Error during bulk delete: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleBulkReprocess = async () => {
    if (selectedInvoices.size === 0) return

    setShowBulkReprocessConfirm(false)
    setIsReprocessing(true)

    try {
      const selectedIds = Array.from(selectedInvoices)
      const result = await reprocessInvoicesBulk(selectedIds)

      if (result.succeeded > 0) {
        // Update status to processing for succeeded invoices
        const succeededIds = new Set(
          result.results
            .filter(r => r.status === 'queued')
            .map(r => r.invoice_id)
        )
        setInvoices(prev => prev.map(inv =>
          succeededIds.has(inv.id)
            ? { ...inv, status: 'processing' as const }
            : inv
        ))
        setProcessingInvoices(prev => new Set([...prev, ...succeededIds]))
        setIsPolling(true)
        setSelectedInvoices(new Set())
        await fetchPendingReviewData()
      }

      if (result.failed > 0) {
        alert(`Reprocessing started for ${result.succeeded} invoice(s). ${result.failed} failed.`)
      }
    } catch (error) {
      console.error('Error in bulk reprocess:', error)
      alert(`Error during bulk reprocess: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsReprocessing(false)
    }
  }

  const handleSingleReprocess = async (invoiceId: string) => {
    try {
      await reprocessInvoice(invoiceId)
      setInvoices(prev => prev.map(inv =>
        inv.id === invoiceId
          ? { ...inv, status: 'processing' as const }
          : inv
      ))
      setProcessingInvoices(prev => new Set([...prev, invoiceId]))
      setIsPolling(true)
      await fetchPendingReviewData()
    } catch (error) {
      alert(`Failed to reprocess: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  const handleReturnToReview = async (invoiceId: string) => {
    setIsReturningToReview(true)
    try {
      const response = await apiClient.fetch(`/invoices/${invoiceId}/return-to-review`, {
        method: 'POST',
      })

      if (response.ok) {
        setInvoices(prev => prev.map(inv =>
          inv.id === invoiceId
            ? { ...inv, status: 'review_required' as const }
            : inv
        ))
        toast({ title: 'Invoice returned to review queue.' })
        await fetchPendingReviewData()
      } else {
        const data = await response.json().catch(() => ({}))
        toast({
          title: 'Failed to return invoice to review',
          description: data?.detail ?? `Error ${response.status}`,
          variant: 'destructive',
        })
      }
    } catch (error) {
      toast({
        title: 'Failed to return invoice to review',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      })
    } finally {
      setIsReturningToReview(false)
      setShowReturnToReviewConfirm(false)
      setReturnToReviewInvoiceId(null)
    }
  }

  const downloadInvoice = async (invoiceId: string) => {
    try {
      const response = await apiClient.fetch(`/invoices/${invoiceId}/download`)

      if (response.ok) {
        const data = await response.json()
        const fileResponse = await fetch(data.download_url)
        if (fileResponse.ok) {
          const blob = await fileResponse.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = data.filename || `invoice_${invoiceId}.pdf`
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        }
      }
    } catch (error) {
      console.error('Error downloading invoice:', error)
    }
  }

  const viewInvoice = async (invoice: Invoice) => {
    setSelectedInvoice(invoice)
    setViewerModalOpen(true)
    setViewerFileViewer({
      isLoadingFile: true,
      fileError: null,
      fileUrl: null
    })
    setViewerZoom(prev => ({ ...prev, zoomLevel: 100 }))

    try {
      const response = await apiClient.fetch(`/invoices/${invoice.id}/download`)

      if (response.ok) {
        const data = await response.json()
        setViewerFileViewer({
          isLoadingFile: false,
          fileError: null,
          fileUrl: data.download_url
        })
      } else {
        const detail = await response.text().catch(() => '')
        setViewerFileViewer({
          isLoadingFile: false,
          fileError: `Failed to load invoice: ${response.status} ${response.statusText}${detail ? ` - ${detail}` : ''}`,
          fileUrl: null
        })
      }
    } catch (error) {
      console.error('Error viewing invoice:', error)
      setViewerFileViewer({
        isLoadingFile: false,
        fileError: error instanceof Error ? error.message : 'Failed to load invoice',
        fileUrl: null
      })
    }
  }

  // Selection and batch operations
  const toggleInvoiceSelection = (invoiceId: string, index: number, shiftKey: boolean) => {
    setSelectedInvoices(prev => {
      const newSelection = new Set(prev)

      if (shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index)
        const end = Math.max(lastSelectedIndex.current, index)
        for (let i = start; i <= end; i++) {
          newSelection.add(filteredInvoices[i].id)
        }
      } else {
        if (newSelection.has(invoiceId)) {
          newSelection.delete(invoiceId)
        } else {
          newSelection.add(invoiceId)
        }
      }

      return newSelection
    })
    lastSelectedIndex.current = index
  }

  const toggleAllInvoices = () => {
    const currentPageIds = filteredInvoices.map(inv => inv.id)
    const allCurrentSelected = currentPageIds.every(id => selectedInvoices.has(id))

    if (allCurrentSelected) {
      // Remove current page from selection
      setSelectedInvoices(prev => {
        const next = new Set(prev)
        currentPageIds.forEach(id => next.delete(id))
        return next
      })
    } else {
      // Add current page to selection
      setSelectedInvoices(prev => {
        const next = new Set(prev)
        currentPageIds.forEach(id => next.add(id))
        return next
      })
    }
  }

  const handleReviewAllPending = async () => {
    if (selectedInvoices.size > 0) {
      // Review selected invoices
      const selectedIds = Array.from(selectedInvoices)
      if (selectedIds.length > 0 && onInvoiceReview) {
        // Store selected invoices in localStorage for persistence
        localStorage.setItem('selectedInvoices', JSON.stringify(selectedIds))
        onInvoiceReview(selectedIds[0], { type: 'selected', invoiceIds: selectedIds })
      }
    } else {
      let pendingInvoices = invoices.filter(inv => inv.status === 'review_required')

      if (pendingInvoices.length === 0) {
        pendingInvoices = pendingReviewInvoices
      }

      if (pendingInvoices.length === 0) {
        pendingInvoices = await fetchPendingReviewData()
      }

      if (pendingInvoices.length > 0 && onInvoiceReview) {
        const pendingIds = pendingInvoices.map(inv => inv.id)
        localStorage.removeItem('selectedInvoices')
        onInvoiceReview(pendingInvoices[0].id, { type: 'all_pending', invoiceIds: pendingIds })
      }
    }
  }

  const handleSingleReview = (invoiceId: string) => {
    if (onInvoiceReview) {
      const pendingInvoicesCount = pendingReviewInvoices.length || pendingReviewTotal
      localStorage.setItem('pendingReviewCount', pendingInvoicesCount.toString())

      // Navigate to review the specific invoice
      onInvoiceReview(invoiceId)
    }
  }

  // Sort invoices (filtering is now server-side)
  const filteredInvoices = React.useMemo(() => {
    const sorted = [...invoices]

    sorted.sort((a, b) => {
      let comparison = 0

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime()
          break
        case 'name':
          comparison = a.original_filename.localeCompare(b.original_filename)
          break
        case 'confidence':
          comparison = (a.overall_confidence || 0) - (b.overall_confidence || 0)
          break
      }

      return sortOrder === 'desc' ? -comparison : comparison
    })

    return sorted
  }, [invoices, sortBy, sortOrder])

  // Clear selections when filters change (not on page or sort change)
  useEffect(() => {
    setSelectedInvoices(new Set())
    lastSelectedIndex.current = null
  }, [filterKey])

  const handleInvoiceExport = useCallback(async (invoiceId: string, filename?: string) => {
    if (exportingInvoices.has(invoiceId)) {
      return
    }

    setExportingInvoices(prev => {
      const next = new Set(prev)
      next.add(invoiceId)
      return next
    })

    try {
      await exportInvoice(invoiceId, filename)
      await loadInvoices()
    } catch (error) {
      console.error('Error exporting invoice:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to export invoice. Please try again.'
      toast({
        title: 'Export failed',
        description: errorMessage,
        variant: 'destructive'
      })
    } finally {
      setExportingInvoices(prev => {
        const next = new Set(prev)
        next.delete(invoiceId)
        return next
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportingInvoices, toast])

  const handleBatchExport = async () => {
    // When cross-page selections exist, send all selected IDs — backend filters by status
    const idsToExport = selectedInvoices.size > 0
      ? Array.from(selectedInvoices)
      : invoices.filter(inv => inv.status === 'reviewed' || inv.status === 'exported').map(inv => inv.id)

    if (idsToExport.length === 0) {
      alert(selectedInvoices.size > 0 ? 'No selected invoices available for export.' : 'No reviewed invoices available for export.')
      return
    }

    setIsExporting(true)
    try {
      const response = await apiClient.fetch('/invoices/batch-export', {
        method: 'POST',
        body: JSON.stringify({
          invoice_ids: idsToExport
        })
      })

      if (response.ok) {
        const data = await response.json()

        // Download the file programmatically with authentication
        try {
          const downloadPath = data.download_url.startsWith('/api/v1')
            ? data.download_url.substring('/api/v1'.length)
            : data.download_url

          // Use apiClient.fetch() which handles base URL and authentication automatically
          const downloadResponse = await apiClient.fetch(downloadPath)

          if (downloadResponse.ok) {
            const blob = await downloadResponse.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = data.filename || 'batch_export.zip'
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
          } else {
            alert('Download failed: Unable to retrieve the export file.')
          }
        } catch (downloadError) {
          console.error('Error downloading file:', downloadError)
          alert('Download failed: Network error occurred.')
        }
      } else {
        const errorText = await response.text()
        console.error('Batch export failed:', response.status, errorText)
        alert(`Batch export failed: ${response.status} ${response.statusText}`)
      }
    } catch (error) {
      console.error('Error in batch export:', error)
      alert(`Error during batch export: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsExporting(false)
    }
  }

  const handleBatchExportExcel = async () => {
    // When cross-page selections exist, send all selected IDs — backend filters by status
    const idsToExport = selectedInvoices.size > 0
      ? Array.from(selectedInvoices)
      : invoices.filter(inv => inv.status === 'reviewed' || inv.status === 'exported').map(inv => inv.id)

    if (idsToExport.length === 0) {
      alert(selectedInvoices.size > 0 ? 'No selected invoices available for export.' : 'No reviewed invoices available for export.')
      return
    }

    setIsExportingExcel(true)
    try {
      const response = await apiClient.fetch('/invoices/batch-export-excel', {
        method: 'POST',
        body: JSON.stringify({
          invoice_ids: idsToExport
        })
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const disposition = response.headers.get('Content-Disposition')
        const filenameMatch = disposition?.match(/filename="?([^"]+)"?/)
        a.download = filenameMatch?.[1] || 'invoices_export.xlsx'
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } else {
        const errorText = await response.text()
        console.error('Excel export failed:', response.status, errorText)
        alert(`Excel export failed: ${response.status} ${response.statusText}`)
      }
    } catch (error) {
      console.error('Error in Excel export:', error)
      alert(`Error during Excel export: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsExportingExcel(false)
    }
  }

  const getBadgeStyles = (status: Invoice['status']) => {
    return getInvoiceStatusBadge(status)
  }

  const getBadgeTooltip = (invoice: Invoice) => {
    if (invoice.status !== 'failed') {
      return undefined
    }

    const message = invoice.error_message?.trim()
    if (!message) {
      return 'Processing failed. No additional details provided.'
    }

    return message.replace(/\s+/g, ' ')
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2 animate-spin" />
          <p className="text-muted-foreground">Loading invoices...</p>
        </div>
      </div>
    )
  }

  // Calculate pending review count based on selection
  const pendingReviewCount = selectedInvoices.size > 0
    ? selectedInvoices.size
    : (pendingReviewTotal || invoices.filter(inv => inv.status === 'review_required').length)

  // Calculate exportable count (reviewed or exported invoices)
  const exportableCount = invoices.filter(inv => inv.status === 'reviewed' || inv.status === 'exported').length

  // Calculate selected exportable invoices count
  // With cross-page selections, off-page invoice statuses are unknown — show total selected count
  const selectedExportableCount = selectedInvoices.size

  return (
    <main className={cn("flex flex-col w-full items-start gap-6 px-0 relative", className)}>
      <section className="flex flex-col items-start gap-4 py-0 relative flex-1 self-stretch w-full grow">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2 page-main-heading">Invoices</h1>
        </div>

        {/* Upload Section */}
        <div className="flex flex-col items-end gap-6 px-0 py-6 relative self-stretch w-full flex-[0_0_auto] bg-neutral-100 rounded-xl translate-y-[-1rem] [--animation-delay:200ms]">
          <div className="flex items-center px-6 py-0 relative self-stretch w-full flex-[0_0_auto]">
            <InvoiceUploadSection
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              isUploading={isUploading}
              setIsUploading={setIsUploading}
              onFilesUploaded={(files) => {
                if (files.length > 0) {
                  setProcessingInvoices(prev => {
                    const updated = new Set(prev)
                    files.forEach(file => {
                      updated.add(file.invoiceId)
                    })
                    return updated
                  })
                  setIsPolling(true)
                }
                // Refresh the table to show newly uploaded invoices
                loadInvoices()
                if (onFilesUploaded) {
                  onFilesUploaded(files)
                }
              }}
              maxFiles={maxFiles}
            />
          </div>


          {/* Legend */}
          <div className="flex items-center justify-between px-6 py-0 relative self-stretch w-full flex-[0_0_auto]">
            <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
              {statusBadgesList.map((badge, index) => (
                <Badge
                  key={index}
                  className={cn(
                    "h-[22px] px-2.5 py-0.5 rounded-lg border border-solid",
                    badge.bgColor,
                    badge.borderColor
                  )}
                >
                  <div className={cn(
                    "relative w-fit  font-text-xs-medium font-[number:var(--text-xs-medium-font-weight)] text-[length:var(--text-xs-medium-font-size)] tracking-[var(--text-xs-medium-letter-spacing)] leading-[var(--text-xs-medium-line-height)] whitespace-nowrap [font-style:var(--text-xs-medium-font-style)]",
                    badge.textColor
                  )}>
                    {badge.label}
                  </div>
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2 page-main-heading">Processing</h2>
        {/* Action Buttons Section */}
        <div className="flex items-center justify-between px-6 py-6 relative self-stretch w-full flex-[0_0_auto] bg-neutral-100 rounded-xl translate-y-[-1rem] [--animation-delay:300ms]">
          {/* Review Pending Button - Left side */}
          <div className="flex items-center">
            <Button
              onClick={handleReviewAllPending}
              disabled={pendingReviewCount === 0}
              className="h-9 rounded-lg bg-[#0000ff] px-4 py-2 text-sm font-medium leading-5 text-neutral-50 shadow-box-shadow-shadow-xs hover:bg-[#3333ff] disabled:opacity-50"
            >
              {selectedInvoices.size > 0 ? (
                <>
                  Review Selected{' '}
                  <span className="inline-flex items-center px-2 py-0.5 ml-1 rounded-full bg-red-600 text-white text-xs font-semibold">
                    {selectedInvoices.size}
                  </span>
                </>
              ) : (
                <>
                  Review All Pending{' '}
                  <span className="inline-flex items-center px-2 py-0.5 ml-1 rounded-full bg-red-600 text-white text-xs font-semibold">
                    {pendingReviewCount}
                  </span>
                </>
              )}
            </Button>
          </div>

          {/* Export and Delete Buttons - Right side */}
          <div className="flex items-center gap-3">
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  disabled={exportableCount === 0 || isExporting || isExportingExcel}
                  className="h-9 px-4 py-2 min-w-[170px] bg-[#0000ff] text-white rounded-lg shadow-box-shadow-shadow-xs disabled:opacity-50 hover:bg-[#3333ff]"
                >
                  {(isExporting || isExportingExcel) ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      <span className="whitespace-nowrap">
                        Export{selectedInvoices.size > 0 ? ` (${selectedExportableCount})` : ' all'}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleBatchExport()} disabled={isExporting} className="text-indigo-700 focus:text-indigo-800 focus:bg-indigo-50">
                  <FileCode className="w-4 h-4 mr-2 text-indigo-500" />
                  Export to XML
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBatchExportExcel()} disabled={isExportingExcel} className="text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-500" />
                  Export to Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Reprocess Selected Button */}
            <Button
              onClick={() => setShowBulkReprocessConfirm(true)}
              disabled={selectedInvoices.size === 0 || isReprocessing}
              variant="outline"
              className="h-9 px-4 py-2 min-w-[170px] border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-400"
            >
              {isReprocessing ? (
                <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reprocess Selected
                </>
              )}
            </Button>

            {/* Delete Selected Button */}
            <Button
              onClick={() => handleBulkAction('delete')}
              disabled={selectedInvoices.size === 0 || isDeleting}
              variant="danger"
              className="min-w-[140px]"
            >
              {isDeleting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span>Delete Selected</span>
              )}
            </Button>
          </div>
        </div>

        {/* Invoice Table */}
        <div className="flex flex-col items-end relative self-stretch w-full flex-[0_0_auto] translate-y-[-1rem] [--animation-delay:400ms]">
          {/* Table Controls */}
          {/* Filter Bar */}
          <div className="flex flex-wrap items-end justify-end gap-x-2 gap-y-3 px-0 py-4 relative self-stretch w-full">
            {/* Tabs (left-aligned, filters flow right) */}
            <div className="mr-auto self-end">
              <Tabs
                value={filters.mirage_followup === 'pending' ? 'pending' : 'all'}
                onValueChange={(v) => updateFilter('mirage_followup', v as 'all' | 'pending')}
              >
                <TabsList className="bg-neutral-100">
                  <TabsTrigger
                    value="all"
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  >
                    All
                  </TabsTrigger>
                  <TabsTrigger
                    value="pending"
                    className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  >
                    To finish in Mirage
                    {mirageFollowupPending > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold">
                        {mirageFollowupPending}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Status */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500">Status</label>
              <Select value={filters.status} onValueChange={(v) => updateFilter('status', v)}>
                <SelectTrigger className="w-[120px] h-9">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="review_required">For Review</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="exported">Exported</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Client */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500">Client</label>
              <FilterableSelect
                value={filters.client_id || undefined}
                onValueChange={(v) => updateFilter('client_id', v === 'none' ? '' : v)}
                options={clientOptions}
                placeholder="All clients"
                showClearOption
                clearOptionLabel="All clients"
                className="min-w-[260px] w-[360px]"
                triggerClassName="h-9 w-[260px]"
                renderSelectedValue={(opt) => (
                  <span className="block truncate">
                    {opt.label}
                    {opt.details && (
                      <span className="text-muted-foreground"> ({opt.details})</span>
                    )}
                  </span>
                )}
              />
            </div>

            {/* Invoice Type */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500">Type</label>
              <Select value={filters.invoice_type || '_all'} onValueChange={(v) => updateFilter('invoice_type', v === '_all' ? '' : v)}>
                <SelectTrigger className="w-[110px] h-9">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All types</SelectItem>
                  <SelectItem value="purchase">Purchase</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Контировка */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500">Контировка</label>
              <Select
                value={filters.kontirovka_status}
                onValueChange={(v) => updateFilter('kontirovka_status', v as InvoiceFilters['kontirovka_status'])}
              >
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Всички</SelectItem>
                  <SelectItem value="none">С контировка</SelectItem>
                  <SelectItem value="any_missing">Без контировка</SelectItem>
                  <SelectItem value="dma">Само ДМА</SelectItem>
                  <SelectItem value="manual">Само ръчно</SelectItem>
                  <SelectItem value="mixed">Смесено</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Upload Date Range */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500">Upload date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[170px] h-9 justify-start text-left font-normal">
                    <CalendarDays className="mr-2 h-4 w-4 shrink-0" />
                    {filters.uploaded_from || filters.uploaded_to ? (
                      <span className="truncate">
                        {filters.uploaded_from ? format(parseISO(filters.uploaded_from), 'd MMM yyyy') : '...'}{' - '}
                        {filters.uploaded_to ? format(parseISO(filters.uploaded_to), 'd MMM yyyy') : '...'}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select range</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={
                      filters.uploaded_from
                        ? { from: parseISO(filters.uploaded_from), to: filters.uploaded_to ? parseISO(filters.uploaded_to) : undefined }
                        : undefined
                    }
                    onSelect={(range: DateRange | undefined) => {
                      setFilters(prev => ({
                        ...prev,
                        uploaded_from: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
                        uploaded_to: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
                      }))
                      setPagination(prev => ({ ...prev, page: 1 }))
                    }}
                    numberOfMonths={2}
                    classNames={{
                      day_selected: "bg-blue-600 text-white hover:bg-blue-600 hover:text-white focus:bg-blue-600 focus:text-white",
                      day_range_middle: "aria-selected:bg-blue-100 aria-selected:text-blue-900",
                      day_range_end: "day-range-end bg-blue-600 text-white",
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Invoice Date Range */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500">Invoice date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[170px] h-9 justify-start text-left font-normal">
                    <CalendarDays className="mr-2 h-4 w-4 shrink-0" />
                    {filters.invoice_date_from || filters.invoice_date_to ? (
                      <span className="truncate">
                        {filters.invoice_date_from ? format(parseISO(filters.invoice_date_from), 'd MMM yyyy') : '...'}{' - '}
                        {filters.invoice_date_to ? format(parseISO(filters.invoice_date_to), 'd MMM yyyy') : '...'}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select range</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={
                      filters.invoice_date_from
                        ? { from: parseISO(filters.invoice_date_from), to: filters.invoice_date_to ? parseISO(filters.invoice_date_to) : undefined }
                        : undefined
                    }
                    onSelect={(range: DateRange | undefined) => {
                      setFilters(prev => ({
                        ...prev,
                        invoice_date_from: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
                        invoice_date_to: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
                      }))
                      setPagination(prev => ({ ...prev, page: 1 }))
                    }}
                    numberOfMonths={2}
                    classNames={{
                      day_selected: "bg-blue-600 text-white hover:bg-blue-600 hover:text-white focus:bg-blue-600 focus:text-white",
                      day_range_middle: "aria-selected:bg-blue-100 aria-selected:text-blue-900",
                      day_range_end: "day-range-end bg-blue-600 text-white",
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Search */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500">Search</label>
              <Input
                placeholder="Filename, client, counterparty…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-[180px] h-9"
              />
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-neutral-500 hover:text-neutral-700"
                onClick={() => {
                  setFilters({ ...DEFAULT_FILTERS })
                  setSearchInput('')
                }}
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="flex flex-col items-start relative self-stretch w-full flex-[0_0_auto] bg-white rounded-md border border-solid border-neutral-200">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-200">
                  <th className="w-[40px] h-10 pl-2 pr-0 py-0 text-left">
                    <Checkbox
                      checked={
                        filteredInvoices.length > 0 &&
                          filteredInvoices.every(inv => selectedInvoices.has(inv.id))
                          ? true
                          : filteredInvoices.some(inv => selectedInvoices.has(inv.id))
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={toggleAllInvoices}
                      className="w-4 h-4 bg-white rounded border border-solid border-neutral-200 shadow-box-shadow-shadow-xs"
                    />
                  </th>
                  <th className="w-[100px] h-10 px-2 py-0 text-left">
                    <div className="relative w-fit  font-text-sm-medium font-[number:var(--text-sm-medium-font-weight)] text-neutral-950 text-[length:var(--text-sm-medium-font-size)] tracking-[var(--text-sm-medium-letter-spacing)] leading-[var(--text-sm-medium-line-height)] whitespace-nowrap [font-style:var(--text-sm-medium-font-style)]">
                      Status
                    </div>
                  </th>
                  <th className="h-10 flex-1 text-left">
                    <div className="flex items-center p-2 rounded-md cursor-pointer" onClick={() => {
                      if (sortBy === 'name') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                      } else {
                        setSortBy('name')
                        setSortOrder('asc')
                      }
                    }}>
                      <div className="relative w-fit  font-text-sm-medium font-[number:var(--text-sm-medium-font-weight)] text-neutral-950 text-[length:var(--text-sm-medium-font-size)] tracking-[var(--text-sm-medium-letter-spacing)] leading-[var(--text-sm-medium-line-height)] whitespace-nowrap [font-style:var(--text-sm-medium-font-style)]">
                        Filename
                      </div>
                      <ArrowUpDown className="w-4 h-4 ml-2" />
                    </div>
                  </th>
                  <th className="w-[260px] h-10 text-left">
                    <div className="flex items-center p-2 rounded-md">
                      <div className="relative w-fit font-text-sm-medium font-[number:var(--text-sm-medium-font-weight)] text-neutral-950 text-[length:var(--text-sm-medium-font-size)] tracking-[var(--text-sm-medium-letter-spacing)] leading-[var(--text-sm-medium-line-height)] whitespace-nowrap [font-style:var(--text-sm-medium-font-style)]">
                        Note
                      </div>
                    </div>
                  </th>
                  <th className="w-[200px] h-10 text-left">
                    <div className="flex items-center p-2 rounded-md">
                      <div className="relative w-fit  font-text-sm-medium font-[number:var(--text-sm-medium-font-weight)] text-neutral-950 text-[length:var(--text-sm-medium-font-size)] tracking-[var(--text-sm-medium-letter-spacing)] leading-[var(--text-sm-medium-line-height)] whitespace-nowrap [font-style:var(--text-sm-medium-font-style)]">
                        Client
                      </div>
                    </div>
                  </th>
                  <th className="w-[100px] h-10 text-left">
                    <div className="flex items-center p-2 rounded-md cursor-pointer" onClick={() => {
                      if (sortBy === 'date') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                      } else {
                        setSortBy('date')
                        setSortOrder('desc')
                      }
                    }}>
                      <div className="relative w-fit  font-text-sm-medium font-[number:var(--text-sm-medium-font-weight)] text-neutral-950 text-[length:var(--text-sm-medium-font-size)] tracking-[var(--text-sm-medium-letter-spacing)] leading-[var(--text-sm-medium-line-height)] whitespace-nowrap [font-style:var(--text-sm-medium-font-style)]">
                        Date
                      </div>
                    </div>
                  </th>
                  <th className="w-[310px] h-10 px-2 py-0 text-left">
                    <div className="flex items-center justify-between">
                      <div className="relative w-fit  font-text-sm-medium font-[number:var(--text-sm-medium-font-weight)] text-neutral-950 text-[length:var(--text-sm-medium-font-size)] tracking-[var(--text-sm-medium-letter-spacing)] leading-[var(--text-sm-medium-line-height)] whitespace-nowrap [font-style:var(--text-sm-medium-font-style)]">
                        Actions
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice, index) => {
                  const badgeStyles = getBadgeStyles(invoice.status)
                  const badgeTooltip = getBadgeTooltip(invoice)
                  const tooltipId = badgeTooltip ? `invoice-status-tooltip-${invoice.id}` : undefined
                  const isProcessing = invoice.status === 'processing' || invoice.status === 'uploaded'
                  const showActions = !isProcessing
                  const showClientAndDate = !isProcessing
                  const isReviewAction = invoice.status === 'review_required'
                  const isCurrentlyProcessing = processingInvoices.has(invoice.id)

                  return (
                    <tr
                      key={invoice.id}
                      className={cn(
                        "border-b border-neutral-200 cursor-pointer select-none transition-colors",
                        isCurrentlyProcessing && "bg-blue-50",
                        selectedInvoices.has(invoice.id) ? "bg-blue-50/50" : "hover:bg-blue-50"
                      )}
                      onClick={(e) => {
                        // Don't toggle selection when clicking the actions column
                        const target = e.target as HTMLElement
                        if (target.closest('[data-actions]')) return
                        toggleInvoiceSelection(invoice.id, index, e.shiftKey)
                      }}
                    >
                      <td className="w-[40px] h-[49px] pl-2 pr-0 py-2">
                        <Checkbox
                          checked={selectedInvoices.has(invoice.id)}
                          tabIndex={-1}
                          className="w-4 h-4 bg-white rounded border border-solid border-neutral-200 shadow-box-shadow-shadow-xs pointer-events-none"
                        />
                      </td>
                      <td className="w-[100px] h-[49px] p-2">
                        <div className="flex items-center gap-2">
                          <div className={cn("relative", badgeTooltip && "group cursor-help")}>
                            <Badge
                              tabIndex={badgeTooltip ? 0 : undefined}
                              aria-label={badgeTooltip ? `${badgeStyles.label}. ${badgeTooltip}` : badgeStyles.label}
                              aria-describedby={tooltipId}
                              className={cn(
                                "h-[22px] px-2.5 py-0.5 rounded-lg border border-solid",
                                badgeStyles.bgColor,
                                badgeStyles.borderColor
                              )}
                            >
                              <div className={cn(
                                "relative w-fit  font-text-xs-medium font-[number:var(--text-xs-medium-font-weight)] text-[length:var(--text-xs-medium-font-size)] tracking-[var(--text-xs-medium-letter-spacing)] leading-[var(--text-xs-medium-line-height)] whitespace-nowrap [font-style:var(--text-xs-medium-font-style)]",
                                badgeStyles.textColor
                              )}>
                                {badgeStyles.label}
                              </div>
                            </Badge>
                            {badgeTooltip && (
                              <div
                                id={tooltipId}
                                role="tooltip"
                                className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-max max-w-xs -translate-x-1/2 rounded-md bg-neutral-900 px-3 py-2 text-xs font-medium text-white shadow-lg opacity-0 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0 translate-y-1"
                              >
                                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-full border-4 border-transparent border-b-neutral-900" />
                                <span className="block text-left whitespace-normal break-words">
                                  {badgeTooltip}
                                </span>
                              </div>
                            )}
                          </div>
                          {isCurrentlyProcessing && (
                            <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse"></div>
                          )}
                          {invoice.kontirovka_status && invoice.kontirovka_status !== 'complete' && (
                            <span
                              title={
                                invoice.kontirovka_status === 'dma'
                                  ? 'Ред(ове) с ДМА — експортирани без контировка'
                                  : invoice.kontirovka_status === 'manual'
                                    ? 'Ръчно изчистена контировка'
                                    : 'Смесено: ДМА и ръчно'
                              }
                              className={cn(
                                "h-[20px] px-2 py-0 inline-flex items-center whitespace-nowrap rounded-md text-[10px] font-semibold uppercase tracking-wide border",
                                invoice.kontirovka_status === 'dma'
                                  ? "bg-amber-50 border-amber-200 text-amber-700"
                                  : invoice.kontirovka_status === 'manual'
                                    ? "bg-neutral-100 border-neutral-300 text-neutral-700"
                                    : "bg-orange-50 border-orange-200 text-orange-700",
                              )}
                            >
                              {invoice.kontirovka_status === 'dma'
                                ? 'ДМА'
                                : invoice.kontirovka_status === 'manual'
                                  ? 'без контировка'
                                  : 'ДМА + ръчно'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="h-[49px] p-2 flex-1">
                        <div
                          className="relative w-fit  font-text-sm-regular font-[number:var(--text-sm-regular-font-weight)] text-neutral-950 text-[length:var(--text-sm-regular-font-size)] tracking-[var(--text-sm-regular-letter-spacing)] leading-[var(--text-sm-regular-line-height)] cursor-pointer hover:text-blue-600 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSingleReview(invoice.id)
                          }}
                        >
                          {invoice.original_filename}
                        </div>
                      </td>
                      <td className="w-[260px] h-[49px] p-2" data-notes-cell>
                        <NotesCell
                          invoiceId={invoice.id}
                          latestNote={invoice.latest_note}
                          noteCount={invoice.note_count ?? 0}
                          onMutate={loadInvoices}
                        />
                      </td>
                      <td className={cn("w-[200px] h-[49px] p-2", !showClientAndDate && "opacity-0")}>
                        <div className="relative w-fit  font-text-sm-regular font-[number:var(--text-sm-regular-font-weight)] text-neutral-950 text-[length:var(--text-sm-regular-font-size)] tracking-[var(--text-sm-regular-letter-spacing)] leading-[var(--text-sm-regular-line-height)] whitespace-nowrap [font-style:var(--text-sm-regular-font-style)]">
                          {invoice.client?.mssql_client_id ? (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-default">{invoice.client?.name || 'N/A'}</span>
                                </TooltipTrigger>
                                <TooltipContent>Mirage: {invoice.client.mssql_client_id}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            invoice.client?.name || 'N/A'
                          )}
                        </div>
                      </td>
                      <td className={cn("w-[100px] h-[49px] p-2", !showClientAndDate && "opacity-0")}>
                        <div className="relative w-fit  mr-[-1.00px] font-text-sm-regular font-[number:var(--text-sm-regular-font-weight)] text-neutral-950 text-[length:var(--text-sm-regular-font-size)] tracking-[var(--text-sm-regular-letter-spacing)] leading-[var(--text-sm-regular-line-height)] whitespace-nowrap [font-style:var(--text-sm-regular-font-style)]">
                          {formatDate(invoice.uploaded_at)}
                        </div>
                      </td>
                      <td className="h-[49px] p-2" data-actions>
                        {showActions && (
                          <div className="inline-flex items-center gap-4 relative flex-1 grow">
                            <div className="inline-flex items-center gap-2 relative flex-[0_0_auto] mt-[-1.50px] mb-[-1.50px]">
                              <Button
                                asChild
                                variant="outline"
                                onClick={(e) => {
                                  if (e.button === 1 || e.metaKey || e.ctrlKey || e.shiftKey) {
                                    return;
                                  }
                                  e.preventDefault();
                                  handleSingleReview(invoice.id);
                                }}
                                className={cn(
                                  "h-9 px-4 py-2 min-w-[88px] bg-[#0000ff] text-white border rounded-lg shadow-xs text-sm font-medium text-white",
                                  isReviewAction ? "text-neutral-50 border-[#0000ff]" : "bg-white text-neutral-950"
                                )}
                              >
                                <a href={`/review/${invoice.id}`}>
                                  {isReviewAction ? "Review" : "Change"}
                                </a>
                              </Button>
                              <Button
                                variant="outlineAccent"
                                onClick={() => handleInvoiceExport(invoice.id, invoice.original_filename)}
                                disabled={exportingInvoices.has(invoice.id)}
                                aria-busy={exportingInvoices.has(invoice.id)}
                                className="min-w-[80px]"
                              >
                                {exportingInvoices.has(invoice.id) ? (
                                  <div className="w-4 h-4 border-2 border-neutral-950 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <span>Export</span>
                                )}
                              </Button>
                            </div>
                            <Download
                              className="w-4 h-4 cursor-pointer hover:text-green-600"
                              onClick={() => downloadInvoice(invoice.id)}
                            />
                            <Eye
                              className="w-4 h-4 cursor-pointer hover:text-blue-600"
                              onClick={() => viewInvoice(invoice)}
                            />
                            {invoice.status !== 'processing' && (
                              <RotateCcw
                                className="w-4 h-4 cursor-pointer hover:text-amber-600"
                                onClick={() => { setSingleReprocessInvoiceId(invoice.id); setShowSingleReprocessConfirm(true) }}
                              />
                            )}
                            {(invoice.status === 'exported' || invoice.status === 'reviewed') ? (
                              <Undo2
                                className="w-4 h-4 cursor-pointer hover:text-blue-600"
                                onClick={() => { setReturnToReviewInvoiceId(invoice.id); setShowReturnToReviewConfirm(true) }}
                              />
                            ) : (
                              <Undo2 className="w-4 h-4 opacity-20 pointer-events-none" />
                            )}
                            <Trash2
                              className="w-4 h-4 cursor-pointer hover:text-red-600"
                              onClick={() => confirmDeleteInvoice(invoice.id)}
                            />
                            {filters.mirage_followup === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-3 text-xs border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                                disabled={followupBusy.has(invoice.id)}
                                onClick={() => setMirageFollowup(invoice.id, true)}
                              >
                                {followupBusy.has(invoice.id) ? '...' : 'Done in Mirage'}
                              </Button>
                            )}
                            {filters.mirage_followup !== 'pending' && invoice.mirage_followup_done && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-xs text-neutral-500 hover:text-neutral-800"
                                disabled={followupBusy.has(invoice.id)}
                                onClick={() => setMirageFollowup(invoice.id, false)}
                                title="Върни в списъка за довършване"
                              >
                                Undo Mirage done
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {/* Table Footer - Empty now */}
            <div className="flex items-center px-0 py-4 relative self-stretch w-full flex-[0_0_auto]">
            </div>
          </div>

          {/* Selection Status and Navigation - Outside and below the table */}
          <div className="flex items-center justify-between px-0 py-2 relative self-stretch w-full flex-[0_0_auto]">
            {/* Selection Status and Pagination Info - Left side */}
            <div className="flex items-center gap-4">
              <div className="relative w-fit  font-text-sm-regular font-[number:var(--text-sm-regular-font-weight)] text-neutral-500 text-[length:var(--text-sm-regular-font-size)] tracking-[var(--text-sm-regular-letter-spacing)] leading-[var(--text-sm-regular-line-height)] whitespace-nowrap [font-style:var(--text-sm-regular-font-style)]">
                {selectedInvoices.size} of {pagination.total} row(s) selected.
              </div>
              <div className="relative w-fit  font-text-sm-regular font-[number:var(--text-sm-regular-font-weight)] text-neutral-500 text-[length:var(--text-sm-regular-font-size)] tracking-[var(--text-sm-regular-letter-spacing)] leading-[var(--text-sm-regular-line-height)] whitespace-nowrap [font-style:var(--text-sm-regular-font-style)]">
                Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
              </div>
              {isPolling && (
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <div className="relative w-fit  font-text-sm-regular font-[number:var(--text-sm-regular-font-weight)] text-blue-600 text-[length:var(--text-sm-regular-font-size)] tracking-[var(--text-sm-regular-letter-spacing)] leading-[var(--text-sm-regular-line-height)] whitespace-nowrap [font-style:var(--text-sm-regular-font-style)]">
                    Polling for updates...
                  </div>
                </div>
              )}
            </div>

            {/* Per-page selector and Navigation Buttons - Right side */}
            <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
              <span className="text-sm text-neutral-500">Rows per page</span>
              <Popover open={rowsPerPageOpen} onOpenChange={(open) => {
                if (!open) applyRowsPerPage()
                setRowsPerPageOpen(open)
              }}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-9 w-[70px] justify-between px-3 font-normal">
                    {pagination.limit}
                    <ChevronDown className="ml-1 h-3.5 w-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[140px] p-1" align="end">
                  <div className="flex flex-col">
                    {[25, 50, 75, 100].map((val) => (
                      <button
                        key={val}
                        className={cn(
                          "rounded px-3 py-1.5 text-sm text-left hover:bg-neutral-100",
                          pagination.limit === val && "bg-neutral-100 font-medium"
                        )}
                        onClick={() => {
                          setRowsPerPageInput(String(val))
                          setPagination(prev => ({ ...prev, limit: val, page: 1 }))
                          setRowsPerPageOpen(false)
                        }}
                      >
                        {val}
                      </button>
                    ))}
                    <div className="border-t border-neutral-200 mt-1 pt-1 px-1 pb-1">
                      <Input
                        type="number"
                        min={1}
                        max={500}
                        placeholder="Custom"
                        value={rowsPerPageInput}
                        onChange={(e) => setRowsPerPageInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            applyRowsPerPage()
                            setRowsPerPageOpen(false)
                          }
                        }}
                        className="h-8 w-full text-sm"
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                className="h-9 px-4 py-2 bg-white border border-solid border-neutral-200 shadow-box-shadow-shadow-sm rounded-lg"
                disabled={pagination.page <= 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                <div className="relative w-fit  font-text-sm-medium font-[number:var(--text-sm-medium-font-weight)] text-neutral-950 text-[length:var(--text-sm-medium-font-size)] tracking-[var(--text-sm-medium-letter-spacing)] leading-[var(--text-sm-medium-line-height)] whitespace-nowrap [font-style:var(--text-sm-medium-font-style)]">
                  Previous
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-9 px-4 py-2 bg-white border border-solid border-neutral-200 shadow-box-shadow-shadow-sm rounded-lg"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                <div className="relative w-fit  font-text-sm-medium font-[number:var(--text-sm-medium-font-weight)] text-neutral-950 text-[length:var(--text-sm-medium-font-size)] tracking-[var(--text-sm-medium-letter-spacing)] leading-[var(--text-sm-medium-line-height)] whitespace-nowrap [font-style:var(--text-sm-medium-font-style)]">
                  Next
                </div>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Delete Confirmation Dialog (single & bulk) */}
      <Dialog open={deleteConfirmTarget !== null} onOpenChange={(open) => !open && setDeleteConfirmTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Delete {deleteConfirmTarget === 'bulk'
                    ? `${selectedInvoices.size} Invoice${selectedInvoices.size === 1 ? '' : 's'}`
                    : 'Invoice'}
                </DialogTitle>
                <p className="text-sm text-gray-500 mt-1">
                  This action cannot be undone.
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to permanently delete {deleteConfirmTarget === 'bulk'
                ? `${selectedInvoices.size} selected invoice${selectedInvoices.size === 1 ? '' : 's'}`
                : 'this invoice'}?
              This will remove all associated data and files.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="min-w-[140px]"
              onClick={() => {
                if (deleteConfirmTarget === 'bulk') {
                  void handleBulkDelete()
                } else if (deleteConfirmTarget) {
                  void deleteInvoice(deleteConfirmTarget)
                  setDeleteConfirmTarget(null)
                }
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Forever
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Reprocess Confirmation Dialog */}
      <ReprocessConfirmDialog
        open={showBulkReprocessConfirm}
        onConfirm={() => void handleBulkReprocess()}
        onCancel={() => setShowBulkReprocessConfirm(false)}
        isProcessing={isReprocessing}
        count={selectedInvoices.size}
      />

      {/* Single Reprocess Confirmation Dialog */}
      <ReprocessConfirmDialog
        open={showSingleReprocessConfirm}
        onConfirm={() => {
          setShowSingleReprocessConfirm(false)
          if (singleReprocessInvoiceId) void handleSingleReprocess(singleReprocessInvoiceId)
          setSingleReprocessInvoiceId(null)
        }}
        onCancel={() => { setShowSingleReprocessConfirm(false); setSingleReprocessInvoiceId(null) }}
        isProcessing={isReprocessing}
        count={1}
      />

      {/* Return to Review Confirmation Dialog */}
      <Dialog open={showReturnToReviewConfirm} onOpenChange={(open) => { if (!open) { setShowReturnToReviewConfirm(false); setReturnToReviewInvoiceId(null) } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                <Undo2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Return to Review
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-gray-700">
              This invoice will be returned to the review queue. All extracted data, corrections, and ERP metadata will be preserved. No data will be deleted.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => { setShowReturnToReviewConfirm(false); setReturnToReviewInvoiceId(null) }}
              disabled={isReturningToReview}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (returnToReviewInvoiceId) void handleReturnToReview(returnToReviewInvoiceId)
              }}
              disabled={isReturningToReview}
            >
              {isReturningToReview ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Undo2 className="w-4 h-4 mr-2" />
              )}
              Return to Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Modal */}
      <Dialog open={viewerModalOpen} onOpenChange={setViewerModalOpen}>
        <DialogContent className="max-w-7xl h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-lg font-semibold">
              {selectedInvoice?.original_filename || 'Invoice Viewer'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full overflow-hidden">
            {selectedInvoice && (
              <DocumentViewer
                invoice={{
                  ...selectedInvoice,
                  overall_confidence: selectedInvoice.overall_confidence ?? 0,
                  processed_at: selectedInvoice.processed_at ?? "",
                }}
                fileViewer={viewerFileViewer}
                zoom={viewerZoom}
                className="h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </main>
  )
}
